import LegalLayout, { LegalSection, LegalSubSection } from '@/components/LegalLayout';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';

export default function PrivacyPolicy() {
  return (
    <>
      <SeoHead
        title="Privacy Policy"
        description="How WriteAssess collects, uses, and protects your personal data and pupil data."
        canonical="/privacy"
      />
      <LegalLayout title="Privacy Policy" lastUpdated="5 November 2026">
      <LegalSection title="1. Who We Are">
        <p>
          WriteAssess ("we", "us", "our") operates the WriteAssess platform at writeassess.co.uk. We are the data
          controller for personal data processed through the Service. For data protection enquiries, contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>.
        </p>
        <p>
          This Privacy Policy explains what personal data we collect, why we collect it, how we use it, and your
          rights under UK GDPR and the Data Protection Act 2018. Where WriteAssess processes pupil data on behalf
          of a school, the school acts as data controller and WriteAssess acts as data processor — this relationship
          is governed by our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="2. Data We Collect">
        <LegalSubSection title="2.1 Account holders (teachers and administrators)">
          <ul className="list-disc pl-6 space-y-1">
            <li>Full name and email address</li>
            <li>School name and role</li>
            <li>Billing information (processed by Stripe — we do not store card details)</li>
            <li>Usage data (assessments created, login history)</li>
          </ul>
        </LegalSubSection>
        <LegalSubSection title="2.2 Pupil data (uploaded by teachers)">
          <ul className="list-disc pl-6 space-y-1">
            <li>First name and last name (or anonymised identifier, at the teacher's discretion)</li>
            <li>Year group and class</li>
            <li>Images of handwritten writing samples</li>
            <li>AI-generated assessment results and grades</li>
          </ul>
          <p>
            Pupil data is provided to us by the school acting as data controller. Schools are responsible for
            ensuring they have appropriate grounds to share this data with us. See our{' '}
            <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link> for
            further detail on our role as data processor for pupil data.
          </p>
        </LegalSubSection>
        <LegalSubSection title="2.3 Free sample assessment (no account)">
          <p>
            Anyone can try one free assessment at{' '}
            <Link to="/try" className="text-primary hover:underline">writeassess.co.uk/try</Link> without an
            account. For this we collect:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>First name and email address, and whether the address looks like a work or personal one</li>
            <li>Year group, plus role and school name if you choose to give them</li>
            <li>Whether you ticked the marketing box, when, and the exact wording you agreed to</li>
            <li>The grade your sample received (we don't keep the photo, the transcription or the assessment text)</li>
            <li>A one-way hash of your IP address (never the address itself), used only to stop the form being misused</li>
            <li>Where you came from (for example a blog link or campaign tag), and whether you later create an account</li>
          </ul>
          <p>
            The photo of the writing is sent to OpenAI for analysis and deleted from our storage straight after,
            whatever the outcome. We ask you to confirm the writing contains no pupil names or identifying details
            and that you're allowed to share it. For the free sample, WriteAssess is the data controller, since
            there's no school account involved.
          </p>
        </LegalSubSection>
        <LegalSubSection title="2.4 Technical data">
          <ul className="list-disc pl-6 space-y-1">
            <li>IP address and browser type</li>
            <li>Pages visited and features used</li>
            <li>Error logs and performance data</li>
          </ul>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="3. How We Use Your Data">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
              <th className="text-left py-2 font-semibold text-foreground">Legal basis</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              ['Providing the assessment service', 'Contract (Art. 6(1)(b))'],
              ['Processing payments and managing subscriptions', 'Contract (Art. 6(1)(b))'],
              ['Sending service and account notifications', 'Contract (Art. 6(1)(b))'],
              ['Complying with legal obligations', 'Legal obligation (Art. 6(1)(c))'],
              ['Improving and securing the platform', 'Legitimate interests (Art. 6(1)(f))'],
              ['Sending optional product updates (with consent)', 'Consent (Art. 6(1)(a))'],
              ['Assessing a free sample and emailing you the result', 'Contract (Art. 6(1)(b))'],
              ['Preventing abuse of the free sample (one per email address, limits per IP address)', 'Legitimate interests (Art. 6(1)(f))'],
              ['Occasional emails about relevant WriteAssess products to work email addresses that tried a free sample, always with an unsubscribe link', 'Legitimate interests (Art. 6(1)(f))'],
            ].map(([purpose, basis]) => (
              <tr key={purpose}>
                <td className="py-2 pr-4">{purpose}</td>
                <td className="py-2">{basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          We do not use pupil writing samples or assessment data to train AI models, for advertising purposes, or
          for any purpose other than providing the Service to the school. For full details of how pupil data is
          handled under our data processor obligations, see our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="4. Data Sharing">
        <p>We share data only with trusted sub-processors necessary to deliver the Service:</p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Sub-processor</th>
              <th className="text-left py-2 pr-4 font-semibold text-foreground">Purpose</th>
              <th className="text-left py-2 font-semibold text-foreground">Location</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              ['Supabase', 'Database, authentication, file storage', 'EU / USA (SCCs)'],
              ['OpenAI', 'AI writing analysis via GPT-6 Luna', 'USA (SCCs)'],
              ['Stripe', 'Payment processing', 'USA (SCCs)'],
              ['Pinecone', 'Curriculum exemplar vector search', 'USA (SCCs)'],
              ['n8n', 'Workflow automation', 'EU'],
              ['Mailtrap', 'Sending emails, including free sample results', 'EU / USA (SCCs)'],
              ['Cloudflare', 'Bot protection (Turnstile) on the free sample and contact forms', 'Global (SCCs)'],
            ].map(([name, purpose, location]) => (
              <tr key={name}>
                <td className="py-2 pr-4 font-medium text-foreground">{name}</td>
                <td className="py-2 pr-4">{purpose}</td>
                <td className="py-2">{location}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          Where data is transferred outside the UK/EEA, appropriate safeguards are in place (Standard Contractual
          Clauses or UK IDTA). We do not sell personal data to third parties. For a full description of each
          sub-processor's role, the data they handle, and their data processing agreements, see our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="5. Data Retention">
        <p>
          We retain personal data only for as long as necessary for the purposes for which it was collected,
          or as required by law. The table below summarises our retention periods by data type.
        </p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 font-semibold text-foreground w-1/2">Data type</th>
                <th className="text-left py-2 font-semibold text-foreground">Retention period</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="py-2 pr-4 text-foreground">Pupil records</td>
                <td className="py-2">While account is active, then deleted or anonymised within <strong className="text-foreground">90 days</strong> of account closure</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Writing samples and images</td>
                <td className="py-2"><strong className="text-foreground">3 academic years</strong>, then deleted unless earlier deletion is requested</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Assessments and feedback</td>
                <td className="py-2"><strong className="text-foreground">3 academic years</strong>, then deleted or anonymised</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Assessment chat history</td>
                <td className="py-2"><strong className="text-foreground">3 academic years</strong>, aligned with assessment data</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Contact and support messages</td>
                <td className="py-2"><strong className="text-foreground">2 years</strong></td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Billing and payment records</td>
                <td className="py-2"><strong className="text-foreground">6 years</strong> as required by UK accounting and tax law</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Platform audit logs</td>
                <td className="py-2"><strong className="text-foreground">6 years</strong></td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Free sample details (name, email, grade, consent)</td>
                <td className="py-2">Until you unsubscribe, or <strong className="text-foreground">2 years</strong> after your last contact with us</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Free sample photos</td>
                <td className="py-2">Deleted <strong className="text-foreground">immediately</strong> after assessment</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Free sample IP hashes</td>
                <td className="py-2"><strong className="text-foreground">2 days</strong> in our usage log; the copy on your free sample record is kept with that record</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-foreground">Marketing consent records</td>
                <td className="py-2">While subscribed, plus <strong className="text-foreground">2 years</strong> after unsubscribe</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          You may request early deletion of your data at any time. Individual teachers on personal plans can
          delete their account and all data directly in{' '}
          <strong className="text-foreground">Settings → Danger Zone</strong>. For school accounts or partial
          deletion requests, contact us via our{' '}
          <Link to="/contact" className="text-primary hover:underline">contact form</Link>. Schools should also
          refer to our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>{' '}
          for pupil-data-specific retention obligations.
        </p>
      </LegalSection>

      <LegalSection title="6. Cookies">
        <p>
          We use essential cookies to keep you logged in and maintain your session. See our{' '}
          <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link> for full details.
        </p>
      </LegalSection>

      <LegalSection title="7. Your Rights">
        <p>Under UK GDPR you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong className="text-foreground">Access</strong> the personal data we hold about you</li>
          <li><strong className="text-foreground">Rectification</strong> of inaccurate data</li>
          <li><strong className="text-foreground">Erasure</strong> ("right to be forgotten") — individual teachers on personal plans can exercise this directly in <strong className="text-foreground">Settings → Danger Zone</strong>; all other requests via our contact form</li>
          <li><strong className="text-foreground">Restriction</strong> of processing in certain circumstances</li>
          <li><strong className="text-foreground">Data portability</strong> for data you provided to us</li>
          <li><strong className="text-foreground">Object</strong> to processing based on legitimate interests</li>
          <li><strong className="text-foreground">Withdraw consent</strong> where processing is based on consent</li>
        </ul>
        <p>
          You can stop marketing emails at any time, whatever the legal basis, using the unsubscribe link in any
          email we send or our contact form. Unsubscribing takes one click and doesn't need an account.
        </p>
        <p>
          To exercise any right, contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>. We will respond
          within one month. Schools wishing to exercise rights on behalf of pupils should refer to our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
          You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            ico.org.uk
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="8. Security">
        <p>
          We implement appropriate technical and organisational measures to protect your data, including encryption
          in transit (TLS) and at rest, access controls, and regular security reviews. Writing sample images are
          stored in a private, access-controlled bucket and are not publicly accessible. For a full description of
          our security measures as they apply to pupil data, see our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify account holders of material changes
          by email or in-app notification. The date at the top of this page shows when the policy was last updated.
        </p>
      </LegalSection>

      <LegalSection title="10. Contact">
        <p>
          For privacy-related enquiries:{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>.
          For school data protection enquiries, DPA requests, or sub-processor queries, see our{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>.
        </p>
      </LegalSection>
    </LegalLayout>
    </>
  );
}
